<script setup lang="ts">
    import {
        MODELS,
        DEFAULT_TEMPERATURE,
        DEFAULT_PROMPT,
        DEFAULT_LIMIT,
        FILE_UPLOAD_DEFAULTS,
    } from './constants'
    import { agentStatusGlyph, agentVisibilityGlyph } from '../agentGlyphs'
    import PkCheckboxField from '../PkCheckboxField.vue'
    import PkToolApprovalBadge from './PkToolApprovalBadge.vue'
    import PkLanguageModelCombobox from './PkLanguageModelCombobox.vue'
    import PkReasoningSettings from './PkReasoningSettings.vue'
    import PkWebSearchSettings from './PkWebSearchSettings.vue'
    import PkEditorChannels from './PkEditorChannels.vue'
    import PkEditorPrompt from '../PkEditorPrompt.vue'
    import PkEditorAgentType from '../PkEditorAgentType.vue'
    import PkEditorRadioButtons from '../PkEditorRadioButtons.vue'
    import PkEditorCode from '../PkEditorCode.vue'
    import PkEditorJsonSchema from '../PkEditorJsonSchema.vue'
    import PkCode from './PkCode.vue'
    import PkCopyBlock from './PkCopyBlock.vue'
    import PkInputColor from '../input/PkInputColor.vue'
    import PkEditorMedia from '../PkEditorMedia.vue'
    import PkEditorRepeater from '../PkEditorRepeater.vue'
    import PkInfobox from '../PkInfobox.vue'
    import { useI18n } from 'vue-i18n'
    import {
        type AgentFormSettings,
        type AgentReasoning,
        type AgentWebSearch,
        type LocalizedString,
        type Media,
        type VoiceOption,
        AgentBuiltInTool,
        AgentChannel,
        AgentLoaderStrategy,
        AgentType,
        DEFAULT_WIDGET_URL,
        DEFAULT_MAX_STEP_COUNT,
        WebSearchProvider,
        isPlatformRenderedTool,
        isPresentationTool,
        canRequireApproval,
    } from 'models'
    import {
        computed,
        onBeforeUnmount,
        reactive,
        ref,
        toRefs,
        useId,
        watch,
    } from 'vue'
    import PkFieldset from '../PkFieldset.vue'

    const {
        t: $t,
        n: $n,
        availableLocales,
    } = useI18n({
        useScope: 'global',
    })

    // The four ranking sliders keep their label outside the field, because it
    // carries a tooltip beside it, so `label` cannot hold both. These ids are
    // what ties each label to its own slider.
    const rankingIds = {
        freshnessScale: useId(),
        freshnessWeight: useId(),
        mmrDiversity: useId(),
        mmrCandidatesLimit: useId(),
    }

    const getErrorMessages = (path: string): string[] => {
        if (!props.errors) return []
        const keys = path.split('.')
        let current: unknown = props.errors
        for (const key of keys) {
            if (
                typeof current === 'object' &&
                current !== null &&
                key in (current as Record<string, unknown>)
            ) {
                current = (current as Record<string, unknown>)[key]
            } else {
                return []
            }
        }
        if (
            typeof current === 'object' &&
            current !== null &&
            '_errors' in (current as Record<string, unknown>)
        ) {
            const errors = (current as Record<string, unknown>)['_errors']
            return Array.isArray(errors) ? (errors as string[]) : []
        }
        return []
    }

    const props = defineProps<{
        organizationSlug?: string
        baseUrl?: string
        modelValue?: Record<string, unknown>
        names?: string[] | Record<string, string>
        invalid?: boolean
        invalids?: Record<string, unknown>
        errors?: Record<string, unknown>
        playground?: boolean
        /** Provider voices for the voice picker; the host fetches them. */
        voices?: VoiceOption[]
    }>()

    const { errors } = toRefs(props)

    const agentId = defineModel<string>('agentId', {
        default: '',
    })
    const name = defineModel<string>('name', {
        default: '',
    })
    const type = defineModel<AgentType | undefined | string>('type', {
        default: AgentType.Chat,
    })
    const initialMessage = defineModel<Record<string, string> | undefined>(
        'initialMessage',
        { default: undefined },
    )
    const messagePlaceholder = defineModel<Record<string, string> | undefined>(
        'messagePlaceholder',
        { default: undefined },
    )
    const dismissableNotice = defineModel<Record<string, string> | undefined>(
        'dismissableNotice',
        { default: undefined },
    )
    const footerMessage = defineModel<Record<string, string> | undefined>(
        'footerMessage',
        { default: undefined },
    )
    const model = defineModel<string>('model', {
        default: MODELS[0],
    })
    const temperature = defineModel<number>('temperature', {
        default: DEFAULT_TEMPERATURE,
    })
    const prompt = defineModel<string>('prompt', {
        default: DEFAULT_PROMPT,
    })
    const status = defineModel<string>('status', {
        default: 'active',
    })
    const visibility = defineModel<string>('visibility', {
        default: 'private',
    })
    const limit = defineModel<number>('limit', {
        default: DEFAULT_LIMIT,
    })
    const maxStepCount = defineModel<number>('maxStepCount', {
        default: DEFAULT_MAX_STEP_COUNT,
    })
    const maxOutputTokens = defineModel<number | undefined>('maxOutputTokens', {
        default: undefined,
    })
    const presencePenalty = defineModel<number>('presencePenalty', {
        default: 0,
    })
    const frequencyPenalty = defineModel<number>('frequencyPenalty', {
        default: 0,
    })
    const searchMode = defineModel<string>('searchMode', {
        default: 'balanced',
    })
    const reasoning = defineModel<AgentReasoning>('reasoning', {
        default: undefined,
    })
    const webSearch = defineModel<AgentWebSearch>('webSearch', {
        default: () => ({
            enabled: false,
            provider: WebSearchProvider.Perplexity,
        }),
    })
    // 'auto' is what the schema defaults to (AgentInterfaceSchema). The two
    // disagreed, so an agent saved without a theme read as Auto in the
    // backoffice, where the form data is normalized against the schema, and as
    // Light in the playground, which binds the raw agent.
    const theme = defineModel<string>('theme', {
        default: 'auto',
    })
    const mainColor = defineModel<string>('mainColor', {
        default: '#000000',
    })
    const textColor = defineModel<'auto' | 'white' | 'black'>('textColor', {
        default: 'auto',
    })
    const useColorsForAgentHeader = defineModel<boolean>(
        'useColorsForAgentHeader',
        {
            default: false,
        },
    )
    const welcomePopup = defineModel<boolean>('welcomePopup', {
        default: false,
    })
    // (A) Voluntary self-service sign-in, only meaningful on public agents.
    // Default true so an unset config keeps today's behavior (sign-in shown).
    const interactiveLogin = defineModel<boolean>('interactiveLogin', {
        default: true,
    })
    const floatingButtonPosition = defineModel<'left' | 'right'>(
        'floatingButtonPosition',
        {
            default: 'right',
        },
    )
    const maxMessageLength = defineModel<number | undefined>(
        'maxMessageLength',
        {
            default: undefined,
        },
    )
    const media = defineModel<Media | undefined>('media', {
        default: undefined,
    })
    const minuteRateLimit = defineModel<number>('minuteRateLimit', {
        default: 0,
    })
    const hourRateLimit = defineModel<number>('hourRateLimit', {
        default: 0,
    })
    const dayRateLimit = defineModel<number>('dayRateLimit', {
        default: 0,
    })
    const monthRateLimit = defineModel<number>('monthRateLimit', {
        default: 0,
    })
    const trustedOrigins = defineModel<{ origins?: string[] }>(
        'trustedOrigins',
        {
            default: () => ({ origins: [] }),
        },
    )
    const disabledTools = defineModel<AgentBuiltInTool[]>('disabledTools', {
        default: () => [],
    })
    /**
     * Tools that ask the user to confirm before running
     * (docs/specs/tool-approval-interface.md). Names are canonical tool-set
     * keys, so built-ins go in verbatim; the MCP and API tool pages add their
     * own prefixed entries to the same list.
     */
    const approvalTools = defineModel<string[]>('approvalTools', {
        default: () => [],
    })
    // Cross-agent personalization sub-options (default OFF).
    const useAllUserKb = defineModel<boolean>('useAllUserKb', {
        default: false,
    })
    const useAllUserFiles = defineModel<boolean>('useAllUserFiles', {
        default: false,
    })
    const formMaxSteps = defineModel<AgentFormSettings['maxSteps']>(
        'formMaxSteps',
        { default: 5 },
    )
    const formMaxOptionsPerStep = defineModel<
        AgentFormSettings['maxOptionsPerStep']
    >('formMaxOptionsPerStep', { default: 5 })
    const widgetUrl = defineModel<string | undefined>('widgetUrl', {
        default: undefined,
    })
    const loadDelay = defineModel<number | undefined>('loadDelay', {
        default: undefined,
    })
    const loaderStrategy = defineModel<AgentLoaderStrategy | undefined>(
        'loaderStrategy',
        { default: undefined },
    )
    const fileUploadEnabled = defineModel<boolean>('fileUploadEnabled', {
        default: false,
    })

    const voiceEnabled = defineModel<boolean>('voiceEnabled', {
        default: false,
    })

    const voiceMaxSessionSeconds = defineModel<number>(
        'voiceMaxSessionSeconds',
        { default: 600 },
    )
    const voiceId = defineModel<string | undefined>('voiceId', {
        default: undefined,
    })
    const voiceLanguage = defineModel<string>('voiceLanguage', {
        default: 'it',
    })
    const voiceFirstMessage = defineModel<string | undefined>(
        'voiceFirstMessage',
        { default: undefined },
    )
    const voiceCascadeTimeoutSeconds = defineModel<number>(
        'voiceCascadeTimeoutSeconds',
        { default: 10 },
    )
    const voiceFillerAfterSeconds = defineModel<number | undefined>(
        'voiceFillerAfterSeconds',
        { default: undefined },
    )
    const voiceFillerMessage = defineModel<string | undefined>(
        'voiceFillerMessage',
        { default: undefined },
    )
    const voiceIgnoreBackgroundVoices = defineModel<boolean>(
        'voiceIgnoreBackgroundVoices',
        { default: true },
    )
    const voiceTurnEagerness = defineModel<string>('voiceTurnEagerness', {
        default: 'normal',
    })
    const voiceTurnTimeoutSeconds = defineModel<number>(
        'voiceTurnTimeoutSeconds',
        { default: 5 },
    )

    /**
     * Languages the provider covers with the low-latency model this transport
     * requires (`eleven_flash_v2_5`). A free text field would let an agent be
     * configured into a combination the provider rejects at session start.
     */
    const VOICE_LANGUAGES = ['it', 'en', 'es', 'fr', 'de', 'pt', 'nl']

    /**
     * Read order of the provider labels, so the same fact sits in the same
     * place on every row and the column can be scanned. `language` is left
     * out: the call language is chosen right below, and the two disagreeing
     * would only raise a question that does not exist.
     */
    const VOICE_LABEL_ORDER = [
        'gender',
        'age',
        'accent',
        'descriptive',
        'description',
        'use_case',
    ]
    const VOICE_LABEL_HIDDEN = ['language']

    /** The provider writes them raw: `middle_aged`, `social_media`. */
    const humanizeVoiceLabel = (value: string) => {
        const words = value.replace(/_/g, ' ')
        return words.charAt(0).toUpperCase() + words.slice(1)
    }

    /**
     * The labels read as a description: a list of first names says nothing
     * about how a voice sounds, and this is what tells two of them apart
     * before the sample is played.
     */
    const voiceDescription = (voice: VoiceOption) => {
        const labels = voice.labels ?? {}
        const keys = [
            ...VOICE_LABEL_ORDER.filter((key) => key in labels),
            // Unknown keys stay, at the end: the provider keeps adding some,
            // and dropping them silently would hide what a voice is
            ...Object.keys(labels).filter(
                (key) =>
                    !VOICE_LABEL_ORDER.includes(key) &&
                    !VOICE_LABEL_HIDDEN.includes(key),
            ),
        ]
        const seen = new Set<string>()
        return keys
            .flatMap((key) => {
                const value = labels[key]?.trim().toLowerCase()
                if (!value || seen.has(value)) {
                    return []
                }
                seen.add(value)
                return [humanizeVoiceLabel(value)]
            })
            .join(' · ')
    }

    const voiceOptions = computed(() =>
        (props.voices ?? []).map((voice) => ({
            label: voice.name,
            value: voice.voiceId,
            description: voiceDescription(voice),
        })),
    )

    /**
     * The description is searchable too: "female" or "narration" is how a
     * voice is looked for here, a name nobody has heard yet is not.
     */
    const searchVoiceOptions = (
        search: string,
        options: { label: string; value: string; description: string }[],
    ) => {
        const term = search.trim().toLowerCase()
        if (!term) {
            return options
        }
        return options.filter((option) =>
            `${option.label} ${option.description}`
                .toLowerCase()
                .includes(term),
        )
    }

    /**
     * Instant voice clones are missing from this list on purpose: the provider
     * refuses an agent that pairs one with a custom LLM, which is our whole
     * design, so offering one would offer a call that cannot start.
     */
    const voicePickerHint = computed(() => $t('hint.voiceId'))

    /** Plays the provider sample, so a voice is chosen by ear and not by name. */
    const voicePreview = ref<HTMLAudioElement>()
    const voicePreviewPlaying = ref(false)
    const stopVoicePreview = () => {
        voicePreview.value?.pause()
        voicePreview.value = undefined
        voicePreviewPlaying.value = false
    }
    /** The same button stops the sample, so a long voice is not sat through. */
    const toggleVoicePreview = () => {
        if (voicePreviewPlaying.value) {
            stopVoicePreview()
            return
        }
        const previewUrl = props.voices?.find(
            (voice) => voice.voiceId === voiceId.value,
        )?.previewUrl
        if (!previewUrl) {
            return
        }
        stopVoicePreview()
        const audio = new Audio(previewUrl)
        audio.addEventListener('ended', stopVoicePreview)
        voicePreview.value = audio
        voicePreviewPlaying.value = true
        void audio.play().catch(stopVoicePreview)
    }
    const hasVoicePreview = computed(() =>
        Boolean(
            props.voices?.find((voice) => voice.voiceId === voiceId.value)
                ?.previewUrl,
        ),
    )
    // A sample still playing over a voice that is no longer selected is a lie
    // about what was picked
    watch(voiceId, stopVoicePreview)
    onBeforeUnmount(stopVoicePreview)

    const fileUploadMaxFileSize = defineModel<number>('fileUploadMaxFileSize', {
        default: FILE_UPLOAD_DEFAULTS.maxFileSizeBytes,
    })
    const fileUploadAllowedMimeTypes = defineModel<string[]>(
        'fileUploadAllowedMimeTypes',
        {
            default: () => [...FILE_UPLOAD_DEFAULTS.allowedMimeTypes],
        },
    )
    const fileUploadMaxFilesPerMessage = defineModel<number>(
        'fileUploadMaxFilesPerMessage',
        {
            default: FILE_UPLOAD_DEFAULTS.maxFilesPerMessage,
        },
    )
    const fileUploadMaxFilesPerChat = defineModel<number>(
        'fileUploadMaxFilesPerChat',
        {
            default: FILE_UPLOAD_DEFAULTS.maxFilesPerChat,
        },
    )
    const customCss = defineModel<string | undefined>('customCss', {
        default: undefined,
    })
    const privacyPolicyNotice = defineModel<LocalizedString | undefined>(
        'privacyPolicyNotice',
        { default: undefined },
    )
    const fallbackModels = defineModel<string[]>('fallbackModels', {
        default: () => [],
    })
    const zeroDataRetention = defineModel<boolean>('zeroDataRetention', {
        default: true,
    })
    const disallowPromptTraining = defineModel<boolean>(
        'disallowPromptTraining',
        { default: true },
    )
    const providerTimeoutMs = defineModel<number | undefined>(
        'providerTimeoutMs',
        { default: undefined },
    )
    const smoothStream = defineModel<boolean | undefined>('smoothStream', {
        default: false,
    })
    const showExtendedSteps = defineModel<boolean | undefined>(
        'showExtendedSteps',
        { default: false },
    )
    const channels = defineModel<AgentChannel[]>('channels', {
        default: () => Object.values(AgentChannel),
    })
    const externalContextSchema = defineModel<
        Record<string, unknown> | undefined
    >('externalContextSchema', { default: undefined })

    // Ranking configuration
    const ranking = defineModel<
        | {
              freshness?: {
                  enabled: boolean
                  scale: number
                  weight: number
              }
              priority?: {
                  enabled: boolean
              }
              mmr?: {
                  enabled: boolean
                  diversity: number
                  candidatesLimit: number
              }
          }
        | undefined
    >('ranking', { default: undefined })

    /**
     * Normalizes the ranking model once: the (reactive) reassignment emits
     * `update:ranking` so the object lands in the bound form data, and every
     * nested field can then be bound directly with v-model.
     */
    watch(
        ranking,
        (value) => {
            if (!value?.freshness || !value?.priority || !value?.mmr) {
                ranking.value = reactive({
                    freshness: {
                        enabled: false,
                        scale: 7776000,
                        weight: 0.1,
                        ...value?.freshness,
                    },
                    priority: { enabled: true, ...value?.priority },
                    mmr: {
                        enabled: false,
                        diversity: 0.3,
                        candidatesLimit: 50,
                        ...value?.mmr,
                    },
                })
            }
        },
        { immediate: true },
    )

    const freshnessScaleDays = computed({
        get: () => {
            const raw = ranking.value?.freshness?.scale ?? 7776000
            // If it's already in seconds (large number), convert to days
            if (raw >= 86400) {
                return Math.round(raw / 86400)
            }
            return raw
        },
        set: (value: number) => {
            if (ranking.value?.freshness) {
                ranking.value.freshness.scale = value * 86400
            }
        },
    })

    const freshnessExplanation = computed(() => {
        const half = freshnessScaleDays.value
        const zero = half * 4
        const months = Math.round((zero / 30.4) * 10) / 10

        const weight = ranking.value?.freshness?.weight ?? 0.1
        const halfWeight = Math.round((weight / 2) * 1000) / 1000
        const zeroWeight = Math.round(weight * 0.06 * 1000) / 1000

        const opt = {
            half,
            zero,
            months,
            weight: weight.toFixed(2),
            halfWeight: halfWeight
                .toFixed(3)
                .replace(/0+$/, '')
                .replace(/\.$/, ''),
            zeroWeight: zeroWeight
                .toFixed(3)
                .replace(/0+$/, '')
                .replace(/\.$/, ''),
        }

        if (zero >= 30) {
            return $t('hint.freshnessScaleExplanationMonths', opt)
        }
        return $t('hint.freshnessScaleExplanationDays', opt)
    })

    const isExternalContextDialogVisible = ref(false)
    const isExternalContextEnabled = computed({
        get: () => externalContextSchema.value != null,
        set: (enabled: boolean) => {
            externalContextSchema.value = enabled ? {} : undefined
        },
    })

    const MIME_TYPE_CATEGORIES: Record<string, string[]> = {
        images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documents: ['application/pdf'],
        text: ['text/plain', 'text/markdown', 'text/csv'],
    }

    const mimeTypeOptions = computed(() => [
        { label: $t('label.mimeTypeImages'), value: 'images' },
        { label: $t('label.mimeTypeDocuments'), value: 'documents' },
        { label: $t('label.mimeTypeText'), value: 'text' },
    ])

    const selectedMimeCategories = computed({
        get: () =>
            Object.entries(MIME_TYPE_CATEGORIES)
                .filter(([, types]) =>
                    types.every((t) =>
                        fileUploadAllowedMimeTypes.value.includes(t),
                    ),
                )
                .map(([key]) => key),
        set: (categories: string[]) => {
            fileUploadAllowedMimeTypes.value = categories.flatMap(
                (cat) => MIME_TYPE_CATEGORIES[cat] ?? [],
            )
        },
    })

    const fileSizeMB = computed({
        get: () => Math.round(fileUploadMaxFileSize.value / (1024 * 1024)),
        set: (val: number) => {
            fileUploadMaxFileSize.value = val * 1024 * 1024
        },
    })

    const toolGroups = computed(() => [
        {
            key: 'knowledgeSearch',
            icon: 'ri:search-line',
            tools: [
                AgentBuiltInTool.SearchInKnowledgeBase,
                AgentBuiltInTool.ShowSources,
                AgentBuiltInTool.RetrieveRevisedAnswers,
                AgentBuiltInTool.ShowWebPages,
                AgentBuiltInTool.ReadDocumentFile,
            ],
        },
        {
            key: 'richContent',
            icon: 'ri:layout-grid-line',
            tools: [
                AgentBuiltInTool.ShowArtifact,
                AgentBuiltInTool.ShowForm,
                AgentBuiltInTool.ShowMultipleChoice,
                AgentBuiltInTool.ShowComparison,
                AgentBuiltInTool.ShowProductList,
                AgentBuiltInTool.ShowImageGallery,
                AgentBuiltInTool.ShowVideo,
                AgentBuiltInTool.ShowQrCode,
                AgentBuiltInTool.ShowSuggestedReply,
                AgentBuiltInTool.ShowDiagram,
            ],
        },
        {
            key: 'communication',
            icon: 'ri:mail-send-line',
            tools: [
                AgentBuiltInTool.ShowEmail,
                AgentBuiltInTool.ShowMessage,
                AgentBuiltInTool.ShowContactForm,
                AgentBuiltInTool.ShowCalendarEvent,
            ],
        },
        {
            key: 'locationWeather',
            icon: 'ri:map-pin-line',
            tools: [
                AgentBuiltInTool.RequestGeolocation,
                AgentBuiltInTool.Geocode,
                AgentBuiltInTool.ShowLocation,
                AgentBuiltInTool.GetWeather,
                AgentBuiltInTool.ShowWeather,
            ],
        },
        {
            key: 'personalization',
            icon: 'ri:user-heart-line',
            tools: [
                AgentBuiltInTool.UserMemory,
                AgentBuiltInTool.SearchInUserFiles,
            ],
        },
        {
            key: 'delegation',
            icon: 'ri:flow-chart',
            tools: [AgentBuiltInTool.RunWorker],
        },
        {
            key: 'channels',
            icon: 'ri:chat-3-line',
            tools: [AgentBuiltInTool.FetchTeamsChannelHistory],
        },
    ])

    const isToolAvailable = (tool: AgentBuiltInTool): boolean => {
        if (
            tool === AgentBuiltInTool.ShowSources ||
            tool === AgentBuiltInTool.ReadDocumentFile
        ) {
            return !disabledTools.value.includes(
                AgentBuiltInTool.SearchInKnowledgeBase,
            )
        }
        if (tool === AgentBuiltInTool.ShowWeather) {
            return !disabledTools.value.includes(AgentBuiltInTool.GetWeather)
        }
        return true
    }

    const fallbackModelExclusions = computed(() =>
        model.value ? [model.value] : [],
    )

    function isToolEnabled(tool: AgentBuiltInTool): boolean {
        return !disabledTools.value.includes(tool)
    }

    /**
     * Whether the tool has anything to configure beyond on/off, so the
     * "advanced options" accordion is worth rendering.
     */
    function hasAdvancedOptions(tool: AgentBuiltInTool): boolean {
        return (
            tool === AgentBuiltInTool.ShowForm ||
            tool === AgentBuiltInTool.UserMemory ||
            tool === AgentBuiltInTool.SearchInUserFiles ||
            canRequireApproval(tool)
        )
    }

    function requiresApproval(tool: AgentBuiltInTool): boolean {
        return approvalTools.value.includes(tool)
    }

    /**
     * Tools this agent has, under approval, that a call will therefore refuse.
     *
     * Filtered against what is actually enabled: an approval left behind by a
     * tool that was switched off later would otherwise warn about something
     * that cannot happen. It does not cover a tool that asks for approval on
     * its own (an MCP or API tool declaring it), which this panel cannot see.
     */
    const approvalToolsOnCall = computed(() =>
        approvalTools.value
            .filter(
                (tool) =>
                    canRequireApproval(tool as AgentBuiltInTool) &&
                    isToolEnabled(tool as AgentBuiltInTool),
            )
            .map((tool) => $t(`agent.tool.${tool}`)),
    )

    function toggleApproval(tool: AgentBuiltInTool, required: boolean): void {
        approvalTools.value = required
            ? [...approvalTools.value, tool]
            : approvalTools.value.filter((t) => t !== tool)
    }

    function toggleTool(tool: AgentBuiltInTool, enabled: boolean): void {
        if (enabled) {
            disabledTools.value = disabledTools.value.filter((t) => t !== tool)
            return
        }
        disabledTools.value = [...disabledTools.value, tool]
        // A disabled tool can't be called, so its approval flag would be a
        // dangling entry in the list.
        toggleApproval(tool, false)
        // Disabling a parent tool turns off its cross-agent sub-option too.
        if (tool === AgentBuiltInTool.UserMemory) {
            useAllUserKb.value = false
        }
        if (tool === AgentBuiltInTool.SearchInUserFiles) {
            useAllUserFiles.value = false
        }
    }

    const models = computed(() => {
        return MODELS.map((model) => ({
            label: model,
            value: model,
        }))
    })

    const activeProvider = computed(() => {
        return model.value?.split('/')[0] ?? ''
    })

    const isChatAgent = computed(() => {
        return type.value === AgentType.Chat
    })

    // tabs
    const tabs = computed(() => {
        const toReturn: Array<{
            tab: string
            label: string
            class?: string
        }> = [
            { tab: 'general', label: $t('label.general') },
            { tab: 'style', label: $t('label.style') },
            {
                tab: 'tools',
                label: $t('label.tools'),
            },
        ]
        if (isChatAgent.value) {
            if (!props.playground) {
                toReturn.push({
                    tab: 'security',
                    label: $t('label.security'),
                    class: errors.value?.['security'] ? 'invalid' : '',
                })
            }
            toReturn.push({
                tab: 'embed',
                label: $t('label.embed'),
            })
        }
        return toReturn
    })

    // embed
    const embedUrl = computed(() => {
        const baseUrl = window.location.origin
        return `${baseUrl}/app/${props.organizationSlug}/${agentId.value}`
    })

    // chat
    const chatUrl = computed(() => {
        const baseUrl = window.location.origin
        return `${baseUrl}/app/${props.organizationSlug}/${agentId.value}/chat`
    })

    // embed code
    const embedCode = computed(() => {
        const open = '<' + 'script'
        const close = '</' + 'script>'
        const src = `${props.baseUrl}/api/v1/public/organizations/${props.organizationSlug}/agents/${agentId.value}/embed.js`
        return `${open} src="${src}" defer>${close}`
    })
    const iframeEmbedCode = computed(() => {
        return `<iframe src="${embedUrl.value}" width="100%" height="600" frameborder="0"
    style="border: 1px solid #e5e7eb; border-radius: 8px;"></iframe>`
    })
</script>

<template>
    <div class="pk-agent-settings-panel">
        <!-- lazy="once": mount each panel on first activation only, the
         eager default mounts every heavy editor at panel open (needs the
         VvTab lazy prop shipped in @volverjs/ui-vue; no-op until then) -->
        <VvTab :items="tabs" lazy="once">
            <template #panel::general>
                <PkEditorAgentType
                    v-model="type"
                    name="type"
                    :label="$t('label.type')"
                    readonly
                    vertical />
                <div class="xl:grid grid-cols-2 gap-x-md items-start">
                    <VvCombobox
                        v-model="status"
                        name="status"
                        :icon="agentStatusGlyph(status)"
                        :label="$t('label.status')"
                        :placeholder="$t('placeholder.select')"
                        :unselectable="false"
                        select-hint-label=""
                        deselect-hint-label=""
                        selected-hint-label=""
                        :options="[
                            { label: $t('agent.status.draft'), value: 'draft' },
                            {
                                label: $t('agent.status.active'),
                                value: 'active',
                            },
                            {
                                label: $t('agent.status.inactive'),
                                value: 'inactive',
                            },
                        ]"
                        :disabled="playground">
                        <template #option="{ option }">
                            <span class="flex items-center gap-8">
                                <VvIcon
                                    :name="
                                        agentStatusGlyph(String(option.value))
                                    "
                                    class="w-16 h-16 shrink-0" />
                                {{ option.label }}
                            </span>
                        </template>
                    </VvCombobox>
                    <VvCombobox
                        v-model="visibility"
                        name="visibility"
                        :icon="agentVisibilityGlyph(visibility)"
                        :label="$t('label.visibility')"
                        :placeholder="$t('placeholder.select')"
                        :unselectable="false"
                        select-hint-label=""
                        deselect-hint-label=""
                        selected-hint-label=""
                        :options="[
                            {
                                label: $t('agent.visibility.public'),
                                value: 'public',
                            },
                            {
                                label: $t('agent.visibility.private'),
                                value: 'private',
                            },
                            {
                                label: $t('agent.visibility.organization'),
                                value: 'organization',
                            },
                        ]"
                        :disabled="playground || !isChatAgent">
                        <template #option="{ option }">
                            <span class="flex items-center gap-8">
                                <VvIcon
                                    :name="
                                        agentVisibilityGlyph(
                                            String(option.value),
                                        )
                                    "
                                    class="w-16 h-16 shrink-0" />
                                {{ option.label }}
                            </span>
                        </template>
                    </VvCombobox>
                </div>
                <VvInputText
                    v-model="name"
                    name="name"
                    :label="$t('label.name')"
                    :placeholder="$t('placeholder.insert')" />
                <PkLanguageModelCombobox
                    v-model="model"
                    name="model"
                    :label="$t('label.model')"
                    strategy="fixed"
                    :placeholder="$t('action.select')"
                    :options="models"
                    :unselectable="false" />
                <PkEditorPrompt
                    v-model="prompt"
                    name="prompt"
                    :label="$t('label.prompt')"
                    :placeholder="$t('placeholder.insert')" />
                <VvAccordion
                    :title="$t('label.advancedOptions')"
                    modifiers="details"
                    class="border border-surface-3 rounded">
                    <div class="p-8 flex flex-col">
                        <PkFieldset :legend="$t('label.modelParameters')">
                            <PkLanguageModelCombobox
                                v-model="fallbackModels"
                                name="fallbackModels"
                                multiple
                                strategy="fixed"
                                :label="$t('label.fallbackModels')"
                                :placeholder="$t('placeholder.select')"
                                :exclude-models="fallbackModelExclusions" />
                            <VvInputText
                                v-model="maxOutputTokens"
                                name="maxOutputTokens"
                                type="number"
                                min="0"
                                step="1"
                                :label="$t('label.maxOutputTokens')"
                                :placeholder="$t('placeholder.insert')" />
                            <VvInputRange
                                v-model="temperature"
                                name="temperature"
                                :label="$t('label.temperature')"
                                min="0"
                                max="1"
                                step="0.1"
                                :format-value="$n" />
                            <VvInputRange
                                v-model="presencePenalty"
                                name="presencePenalty"
                                :label="$t('label.presencePenalty')"
                                min="-2"
                                max="2"
                                step="0.1"
                                :format-value="$n" />
                            <VvInputRange
                                v-model="frequencyPenalty"
                                name="frequencyPenalty"
                                :label="$t('label.frequencyPenalty')"
                                min="-2"
                                max="2"
                                step="0.1"
                                :format-value="$n" />
                            <VvInputText
                                v-model="providerTimeoutMs"
                                name="providerTimeoutMs"
                                type="number"
                                min="1000"
                                max="789000"
                                step="1000"
                                class="mb-0"
                                :label="$t('label.providerTimeoutMs')"
                                :placeholder="$t('placeholder.insert')"
                                :invalid="
                                    providerTimeoutMs !== undefined &&
                                    (providerTimeoutMs < 1000 ||
                                        providerTimeoutMs > 789000)
                                "
                                :hint-label="$t('hint.providerTimeoutMs')" />
                        </PkFieldset>
                        <PkFieldset :legend="$t('label.reasoning')">
                            <PkReasoningSettings
                                v-model:reasoning="reasoning"
                                :active-provider />
                            <PkCheckboxField
                                v-model="showExtendedSteps"
                                name="showExtendedSteps"
                                class="mt-md mb-0"
                                switch
                                :label="$t('label.showExtendedSteps')"
                                :hint-label="$t('hint.showExtendedSteps')" />
                        </PkFieldset>
                        <PkFieldset :legend="$t('label.searchAndExecution')">
                            <VvCombobox
                                v-model="searchMode"
                                name="searchMode"
                                icon="ri:search-line"
                                strategy="fixed"
                                :label="$t('label.searchMode')"
                                :placeholder="$t('placeholder.select')"
                                :unselectable="false"
                                :options="[
                                    {
                                        label: $t('agent.searchMode.balanced'),
                                        value: 'balanced',
                                    },
                                    {
                                        label: $t('agent.searchMode.semantic'),
                                        value: 'semantic',
                                    },
                                    {
                                        label: $t('agent.searchMode.keyword'),
                                        value: 'keyword',
                                    },
                                ]" />
                            <VvInputRange
                                v-model="limit"
                                name="limit"
                                :label="$t('label.limit')"
                                min="0"
                                max="10"
                                step="1"
                                :format-value="$n" />
                            <VvInputRange
                                v-model="maxStepCount"
                                name="maxStepCount"
                                :label="$t('label.maxStepCount')"
                                min="1"
                                max="40"
                                step="1"
                                class="mb-0"
                                :format-value="$n" />
                        </PkFieldset>
                        <PkFieldset :legend="$t('label.smartRanking')">
                            <VvCheckbox
                                v-if="ranking?.freshness"
                                v-model="ranking.freshness.enabled"
                                name="freshnessEnabled"
                                switch
                                :value="true"
                                :unchecked-value="false"
                                class="flex-nowrap mb-md">
                                <div class="flex flex-col ml-12 gap-2">
                                    <strong>{{
                                        $t('label.freshnessBoost')
                                    }}</strong>
                                    <small class="text-12 text-word-3">{{
                                        $t('hint.freshnessBoost')
                                    }}</small>
                                </div>
                            </VvCheckbox>
                            <template v-if="ranking?.freshness?.enabled">
                                <div class="mb-md">
                                    <div class="flex items-center gap-xs mb-2">
                                        <label
                                            class="block text-12 text-word-3"
                                            :for="rankingIds.freshnessScale">
                                            {{ $t('hint.freshnessScale') }}
                                        </label>
                                        <div
                                            class="info-icon relative cursor-help text-word-3">
                                            <VvIcon
                                                name="ri:information-line"
                                                class="w-14 h-14" />
                                            <VvTooltip
                                                position="bottom"
                                                :value="
                                                    $t(
                                                        'hint.freshnessScaleTooltip',
                                                    )
                                                " />
                                        </div>
                                    </div>
                                    <VvInputRange
                                        :id="rankingIds.freshnessScale"
                                        v-model="freshnessScaleDays"
                                        name="freshnessScale"
                                        min="1"
                                        max="365"
                                        step="1"
                                        class="mb-0"
                                        :format-value="$n" />
                                </div>
                                <div class="mb-md">
                                    <div class="flex items-center gap-xs mb-2">
                                        <label
                                            class="block text-12 text-word-3"
                                            :for="rankingIds.freshnessWeight">
                                            {{ $t('label.freshnessWeight') }}
                                        </label>
                                        <div
                                            class="info-icon relative cursor-help text-word-3">
                                            <VvIcon
                                                name="ri:information-line"
                                                class="w-14 h-14" />
                                            <VvTooltip
                                                position="bottom"
                                                :value="
                                                    $t(
                                                        'hint.freshnessWeightTooltip',
                                                    )
                                                " />
                                        </div>
                                    </div>
                                    <VvInputRange
                                        v-if="ranking?.freshness"
                                        :id="rankingIds.freshnessWeight"
                                        v-model="ranking.freshness.weight"
                                        name="freshnessWeight"
                                        min="0.05"
                                        max="0.5"
                                        step="0.05"
                                        class="mb-0"
                                        :format-value="$n" />
                                    <p
                                        class="text-12 text-word-3 mt-xs leading-normal">
                                        {{ freshnessExplanation }}
                                    </p>
                                </div>
                            </template>
                            <VvCheckbox
                                v-if="ranking?.mmr"
                                v-model="ranking.mmr.enabled"
                                name="mmrEnabled"
                                switch
                                :value="true"
                                :unchecked-value="false"
                                class="flex-nowrap mb-md border-t border-surface-3 pt-md">
                                <div class="flex flex-col ml-12 gap-2">
                                    <strong>{{ $t('label.mmr') }}</strong>
                                    <small class="text-12 text-word-3">{{
                                        $t('hint.mmr')
                                    }}</small>
                                </div>
                            </VvCheckbox>
                            <template v-if="ranking?.mmr?.enabled">
                                <div class="mb-md">
                                    <div class="flex items-center gap-xs mb-2">
                                        <label
                                            class="block text-12 text-word-3"
                                            :for="rankingIds.mmrDiversity">
                                            {{ $t('label.mmrDiversity') }}
                                        </label>
                                        <div
                                            class="info-icon relative cursor-help text-word-3">
                                            <VvIcon
                                                name="ri:information-line"
                                                class="w-14 h-14" />
                                            <VvTooltip
                                                position="bottom"
                                                :value="
                                                    $t(
                                                        'hint.mmrDiversityTooltip',
                                                    )
                                                " />
                                        </div>
                                    </div>
                                    <VvInputRange
                                        v-if="ranking?.mmr"
                                        :id="rankingIds.mmrDiversity"
                                        v-model="ranking.mmr.diversity"
                                        name="mmrDiversity"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        class="mb-0"
                                        :format-value="$n" />
                                </div>
                                <div class="mb-md">
                                    <div class="flex items-center gap-xs mb-2">
                                        <label
                                            class="block text-12 text-word-3"
                                            :for="
                                                rankingIds.mmrCandidatesLimit
                                            ">
                                            {{ $t('label.mmrCandidatesLimit') }}
                                        </label>
                                        <div
                                            class="info-icon relative cursor-help text-word-3">
                                            <VvIcon
                                                name="ri:information-line"
                                                class="w-14 h-14" />
                                            <VvTooltip
                                                position="bottom"
                                                :value="
                                                    $t(
                                                        'hint.mmrCandidatesLimitTooltip',
                                                    )
                                                " />
                                        </div>
                                    </div>
                                    <VvInputRange
                                        v-if="ranking?.mmr"
                                        :id="rankingIds.mmrCandidatesLimit"
                                        v-model="ranking.mmr.candidatesLimit"
                                        name="mmrCandidatesLimit"
                                        min="20"
                                        max="100"
                                        step="10"
                                        class="mb-0"
                                        :format-value="$n" />
                                </div>
                            </template>
                        </PkFieldset>
                        <PkFieldset :legend="$t('label.privacy')" class="mb-0">
                            <VvCheckbox
                                v-model="zeroDataRetention"
                                name="zeroDataRetention"
                                switch
                                :value="true"
                                :unchecked-value="false"
                                class="flex-nowrap mb-0">
                                <div class="flex flex-col ml-12 gap-2">
                                    <strong>{{
                                        $t('label.zeroDataRetention')
                                    }}</strong>
                                    <small class="text-12 text-word-3">{{
                                        $t('hint.zeroDataRetention')
                                    }}</small>
                                </div>
                            </VvCheckbox>
                            <VvCheckbox
                                v-if="!zeroDataRetention"
                                v-model="disallowPromptTraining"
                                name="disallowPromptTraining"
                                switch
                                :value="true"
                                :unchecked-value="false"
                                class="flex-nowrap mt-md mb-0">
                                <div class="flex flex-col ml-12 gap-2">
                                    <strong>{{
                                        $t('label.disallowPromptTraining')
                                    }}</strong>
                                    <small class="text-12 text-word-3">{{
                                        $t('hint.disallowPromptTraining')
                                    }}</small>
                                </div>
                            </VvCheckbox>
                        </PkFieldset>
                        <PkFieldset
                            v-if="!playground"
                            :legend="$t('label.voice')"
                            class="mb-0 mt-md">
                            <VvCheckbox
                                v-model="voiceEnabled"
                                name="voiceEnabled"
                                switch
                                :value="true"
                                :unchecked-value="false"
                                class="flex-nowrap mb-0">
                                <div class="flex flex-col ml-12 gap-2">
                                    <strong>{{
                                        $t('label.voiceEnabled')
                                    }}</strong>
                                    <small class="text-12 text-word-3">{{
                                        $t('hint.voiceEnabled')
                                    }}</small>
                                </div>
                            </VvCheckbox>
                            <!-- What the caller hears, and nothing else: the
                             knobs that shape how the agent listens and waits
                             live under the advanced disclosure, where they can
                             be ignored until a room needs them -->
                            <template v-if="voiceEnabled">
                                <!-- Approval is a screen affordance: on a call
                                 there is nobody to ask, so the backend refuses
                                 those tools instead of hanging the turn. Said
                                 here because otherwise the operator finds out
                                 mid-call, and only for approvals that are on a
                                 tool this agent actually has. -->
                                <PkInfobox
                                    v-if="approvalToolsOnCall.length"
                                    level="warning"
                                    class="mt-md mb-0">
                                    {{
                                        $t('hint.voiceApprovalRefused', {
                                            tools: approvalToolsOnCall.join(
                                                ', ',
                                            ),
                                        })
                                    }}
                                </PkInfobox>
                                <div class="flex items-end gap-sm mt-md">
                                    <VvCombobox
                                        v-model="voiceId"
                                        name="voiceId"
                                        icon="ri:user-voice-line"
                                        strategy="fixed"
                                        class="flex-1 mb-0"
                                        searchable
                                        :label="$t('label.voiceId')"
                                        :hint-label="voicePickerHint"
                                        :placeholder="$t('placeholder.select')"
                                        select-hint-label=""
                                        deselect-hint-label=""
                                        selected-hint-label=""
                                        :options="voiceOptions"
                                        :search-function="searchVoiceOptions">
                                        <template #after>
                                            <VvButton
                                                modifiers="action-quiet"
                                                :icon="
                                                    voicePreviewPlaying
                                                        ? 'ri:stop-circle-line'
                                                        : 'ri:play-circle-line'
                                                "
                                                :disabled="!hasVoicePreview"
                                                :title="
                                                    $t(
                                                        voicePreviewPlaying
                                                            ? 'action.stopVoicePreview'
                                                            : 'action.playVoicePreview',
                                                    )
                                                "
                                                @click="toggleVoicePreview()" />
                                        </template>
                                        <template #option="{ option }">
                                            <span
                                                class="flex flex-col gap-2 min-w-0">
                                                <span class="truncate">{{
                                                    option.label
                                                }}</span>
                                                <small
                                                    v-if="option.description"
                                                    class="text-12 text-word-3 truncate">
                                                    {{ option.description }}
                                                </small>
                                            </span>
                                        </template>
                                    </VvCombobox>
                                </div>
                                <VvCombobox
                                    v-model="voiceLanguage"
                                    name="voiceLanguage"
                                    icon="ri:translate-2"
                                    strategy="fixed"
                                    class="mb-0 mt-md"
                                    :label="$t('label.voiceLanguage')"
                                    :hint-label="$t('hint.voiceLanguage')"
                                    :placeholder="$t('placeholder.select')"
                                    :unselectable="false"
                                    select-hint-label=""
                                    deselect-hint-label=""
                                    selected-hint-label=""
                                    :options="
                                        VOICE_LANGUAGES.map((code) => ({
                                            label: $t(`language.${code}`),
                                            value: code,
                                        }))
                                    " />
                                <VvInputText
                                    v-model="voiceFirstMessage"
                                    name="voiceFirstMessage"
                                    maxlength="300"
                                    class="mb-0 mt-md"
                                    :label="$t('label.voiceFirstMessage')"
                                    :hint-label="$t('hint.voiceFirstMessage')"
                                    :placeholder="$t('placeholder.insert')" />
                                <VvAccordion
                                    :title="$t('label.advancedOptions')"
                                    modifiers="details"
                                    class="border border-surface-3 rounded-lg mt-md">
                                    <div class="p-8 flex flex-col">
                                        <VvCheckbox
                                            v-model="
                                                voiceIgnoreBackgroundVoices
                                            "
                                            name="voiceIgnoreBackgroundVoices"
                                            switch
                                            :value="true"
                                            :unchecked-value="false"
                                            class="flex-nowrap mb-0">
                                            <div
                                                class="flex flex-col ml-12 gap-2">
                                                <strong>{{
                                                    $t(
                                                        'label.voiceIgnoreBackgroundVoices',
                                                    )
                                                }}</strong>
                                                <small
                                                    class="text-12 text-word-3"
                                                    >{{
                                                        $t(
                                                            'hint.voiceIgnoreBackgroundVoices',
                                                        )
                                                    }}</small
                                                >
                                            </div>
                                        </VvCheckbox>
                                        <VvCombobox
                                            v-model="voiceTurnEagerness"
                                            name="voiceTurnEagerness"
                                            icon="ri:hourglass-line"
                                            strategy="fixed"
                                            class="mb-0 mt-md"
                                            :label="
                                                $t('label.voiceTurnEagerness')
                                            "
                                            :hint-label="
                                                $t('hint.voiceTurnEagerness')
                                            "
                                            :placeholder="
                                                $t('placeholder.select')
                                            "
                                            :unselectable="false"
                                            select-hint-label=""
                                            deselect-hint-label=""
                                            selected-hint-label=""
                                            :options="[
                                                {
                                                    label: $t(
                                                        'voiceEagerness.patient',
                                                    ),
                                                    value: 'patient',
                                                },
                                                {
                                                    label: $t(
                                                        'voiceEagerness.normal',
                                                    ),
                                                    value: 'normal',
                                                },
                                                {
                                                    label: $t(
                                                        'voiceEagerness.eager',
                                                    ),
                                                    value: 'eager',
                                                },
                                            ]" />
                                        <div class="flex gap-sm mt-md">
                                            <VvInputText
                                                v-model="
                                                    voiceTurnTimeoutSeconds
                                                "
                                                name="voiceTurnTimeoutSeconds"
                                                type="number"
                                                min="1"
                                                max="30"
                                                step="1"
                                                class="flex-1 mb-0"
                                                :label="
                                                    $t(
                                                        'label.voiceTurnTimeoutSeconds',
                                                    )
                                                "
                                                :placeholder="
                                                    $t('placeholder.insert')
                                                " />
                                            <VvInputText
                                                v-model="
                                                    voiceCascadeTimeoutSeconds
                                                "
                                                name="voiceCascadeTimeoutSeconds"
                                                type="number"
                                                min="5"
                                                max="15"
                                                step="1"
                                                class="flex-1 mb-0"
                                                :label="
                                                    $t(
                                                        'label.voiceCascadeTimeoutSeconds',
                                                    )
                                                "
                                                :placeholder="
                                                    $t('placeholder.insert')
                                                " />
                                        </div>
                                        <small
                                            class="text-12 text-word-3 mt-8 mb-md px-8">
                                            {{
                                                $t(
                                                    'hint.voiceTurnTimeoutSeconds',
                                                )
                                            }}
                                        </small>
                                        <VvInputText
                                            v-model="voiceMaxSessionSeconds"
                                            name="voiceMaxSessionSeconds"
                                            type="number"
                                            min="30"
                                            max="3600"
                                            step="30"
                                            class="mb-0"
                                            :label="
                                                $t(
                                                    'label.voiceMaxSessionSeconds',
                                                )
                                            "
                                            :hint-label="
                                                $t(
                                                    'hint.voiceMaxSessionSeconds',
                                                )
                                            "
                                            :placeholder="
                                                $t('placeholder.insert')
                                            " />
                                        <!-- Said next to the two knobs it is
                                         about, not at the top of the section:
                                         a call on an agent that retrieves is
                                         slow, and the operator finds out from
                                         a customer unless it is written where
                                         the waiting is configured -->
                                        <PkInfobox class="mt-md mb-0">
                                            {{
                                                $t('hint.voiceSlowAgentOnCall')
                                            }}
                                        </PkInfobox>
                                        <VvInputText
                                            v-model="voiceFillerAfterSeconds"
                                            name="voiceFillerAfterSeconds"
                                            type="number"
                                            min="0.5"
                                            max="8"
                                            step="0.5"
                                            class="mb-0 mt-md"
                                            :label="
                                                $t(
                                                    'label.voiceFillerAfterSeconds',
                                                )
                                            "
                                            :hint-label="
                                                $t(
                                                    'hint.voiceFillerAfterSeconds',
                                                )
                                            "
                                            :placeholder="
                                                $t('placeholder.insert')
                                            " />
                                        <VvInputText
                                            v-model="voiceFillerMessage"
                                            name="voiceFillerMessage"
                                            maxlength="200"
                                            class="mb-0 mt-md"
                                            :label="
                                                $t('label.voiceFillerMessage')
                                            "
                                            :hint-label="
                                                $t('hint.voiceFillerMessage')
                                            "
                                            :placeholder="
                                                $t('placeholder.insert')
                                            " />
                                    </div>
                                </VvAccordion>
                            </template>
                        </PkFieldset>
                        <PkEditorChannels
                            v-if="!playground"
                            v-model="channels"
                            class="mb-0 mt-md"
                            :type="type" />
                    </div>
                </VvAccordion>
            </template>
            <template #panel::style>
                <PkEditorMedia
                    v-if="!playground"
                    v-model="media"
                    name="media"
                    :label="$t('label.logo')"
                    :placeholder="$t('placeholder.uploadFile')"
                    :label-add="$t('action.add')"
                    :label-remove="$t('action.remove')"
                    drop-area
                    modifiers="hidden circle profile"
                    accept=".png,.jpg,.jpeg" />
                <PkEditorRadioButtons
                    v-if="isChatAgent"
                    v-model="theme"
                    name="theme"
                    segmented
                    :label="$t('label.theme')"
                    :options="[
                        { label: $t('label.light'), value: 'light' },
                        { label: $t('label.dark'), value: 'dark' },
                        { label: $t('label.auto'), value: 'auto' },
                    ]">
                    <template #light="{ option }">
                        <VvIcon name="ri:sun-line" class="w-16 h-16" />
                        {{ option.label }}
                    </template>
                    <template #dark="{ option }">
                        <VvIcon name="ri:moon-line" class="w-16 h-16" />
                        {{ option.label }}
                    </template>
                    <template #auto="{ option }">
                        <VvIcon name="ri:contrast-line" class="w-16 h-16" />
                        {{ option.label }}
                    </template>
                </PkEditorRadioButtons>
                <PkFieldset :legend="$t('label.appearance')">
                    <PkInputColor
                        v-model="mainColor"
                        name="mainColor"
                        :label="$t('label.mainColor')" />
                    <PkEditorRadioButtons
                        v-model="textColor"
                        name="textColor"
                        segmented
                        :label="$t('label.textColor')"
                        :options="[
                            { label: $t('label.auto'), value: 'auto' },
                            { label: $t('label.white'), value: 'white' },
                            { label: $t('label.black'), value: 'black' },
                        ]"
                        class="mb-0" />
                </PkFieldset>
                <!-- Colours are what a brand actually changes; the rest of the
                 appearance is set once and forgotten, so it waits behind the
                 same disclosure the other sections use -->
                <VvAccordion
                    :title="$t('label.advancedOptions')"
                    modifiers="details"
                    class="border border-surface-3 rounded-lg mt-md mb-md">
                    <div class="p-8 flex flex-col">
                        <PkCheckboxField
                            v-if="isChatAgent"
                            v-model="useColorsForAgentHeader"
                            name="useColorsForAgentHeader"
                            switch
                            class="mb-0"
                            :label="$t('action.useColorsForAgentHeader')"
                            :hint-label="$t('hint.useColorsForAgentHeader')" />
                        <PkCheckboxField
                            v-if="isChatAgent"
                            v-model="smoothStream"
                            name="smoothStream"
                            class="mt-md mb-0"
                            switch
                            :label="$t('label.smoothStream')"
                            :hint-label="$t('hint.smoothStream')" />
                        <PkCheckboxField
                            v-if="!playground && isChatAgent"
                            v-model="welcomePopup"
                            name="welcomePopup"
                            class="mt-md mb-0"
                            switch
                            :label="$t('action.welcomePopup')"
                            :hint-label="$t('hint.welcomePopup')" />
                        <PkCheckboxField
                            v-if="
                                !playground &&
                                isChatAgent &&
                                visibility === 'public'
                            "
                            v-model="interactiveLogin"
                            name="interactiveLogin"
                            class="mt-md mb-0"
                            switch
                            :label="$t('label.interactiveLogin')"
                            :hint-label="$t('hint.interactiveLogin')" />
                        <PkEditorRadioButtons
                            v-if="!playground && isChatAgent"
                            v-model="floatingButtonPosition"
                            name="floatingButtonPosition"
                            segmented
                            :label="$t('label.floatingButtonPosition')"
                            :options="[
                                {
                                    label: $t('label.left'),
                                    value: 'left',
                                },
                                {
                                    label: $t('label.right'),
                                    value: 'right',
                                },
                            ]"
                            class="mt-md mb-0">
                            <template #left="{ option }">
                                <VvIcon
                                    name="ri:layout-left-2-line"
                                    class="w-16 h-16" />
                                {{ option.label }}
                            </template>
                            <template #right="{ option }">
                                <VvIcon
                                    name="ri:layout-right-2-line"
                                    class="w-16 h-16" />
                                {{ option.label }}
                            </template>
                        </PkEditorRadioButtons>
                        <VvInputText
                            v-if="isChatAgent"
                            v-model="maxMessageLength"
                            name="maxMessageLength"
                            type="number"
                            min="1"
                            step="1"
                            class="mt-md mb-0"
                            :label="$t('label.maxMessageLength')"
                            :placeholder="$t('placeholder.insert')" />
                        <PkEditorCode
                            v-if="!playground"
                            v-model="customCss"
                            language="css"
                            class="mt-md mb-0"
                            :label="$t('label.customCss')"
                            :hint-label="$t('hint.customCss')"
                            :placeholder="$t('placeholder.insert')" />
                    </div>
                </VvAccordion>
                <template v-if="isChatAgent">
                    <VvTab
                        :items="
                            availableLocales.map((l) => ({
                                tab: l,
                                label: $t(`language.${l}`),
                            }))
                        "
                        lazy="once"
                        class="border border-surface-3 rounded-xl text-14">
                        <template
                            v-for="locale in availableLocales"
                            #[`panel::${locale}`]
                            :key="locale">
                            <PkEditorPrompt
                                :model-value="initialMessage?.[locale] ?? ''"
                                :name="`initialMessage.${locale}`"
                                :label="$t('label.initialMessage')"
                                :placeholder="
                                    $t(
                                        'message.defaultAgentWelcome',
                                        {},
                                        { locale },
                                    )
                                "
                                @update:model-value="
                                    (val: string | undefined) =>
                                        (initialMessage = {
                                            ...initialMessage,
                                            [locale]: val ?? '',
                                        })
                                " />
                            <VvInputText
                                :model-value="
                                    messagePlaceholder?.[locale] ?? ''
                                "
                                :name="`messagePlaceholder.${locale}`"
                                :label="$t('label.messagePlaceholder')"
                                :placeholder="
                                    $t(
                                        'placeholder.askAnything',
                                        {},
                                        { locale },
                                    )
                                "
                                @update:model-value="
                                    (val: string) =>
                                        (messagePlaceholder = {
                                            ...messagePlaceholder,
                                            [locale]: val,
                                        })
                                " />
                            <PkEditorPrompt
                                :model-value="dismissableNotice?.[locale] ?? ''"
                                :name="`dismissableNotice.${locale}`"
                                :label="$t('label.dismissableNotice')"
                                :placeholder="$t('placeholder.insert')"
                                :hint-label="$t('hint.dismissableNotice')"
                                @update:model-value="
                                    (val: string | undefined) =>
                                        (dismissableNotice = {
                                            ...dismissableNotice,
                                            [locale]: val ?? '',
                                        })
                                " />
                            <PkEditorPrompt
                                :model-value="footerMessage?.[locale] ?? ''"
                                :name="`footerMessage.${locale}`"
                                :label="$t('label.footer')"
                                :placeholder="$t('placeholder.insert')"
                                @update:model-value="
                                    (val: string | undefined) =>
                                        (footerMessage = {
                                            ...footerMessage,
                                            [locale]: val ?? '',
                                        })
                                " />
                            <PkEditorPrompt
                                v-if="
                                    !disabledTools.includes(
                                        AgentBuiltInTool.ShowContactForm,
                                    )
                                "
                                :model-value="
                                    privacyPolicyNotice?.[locale] ?? ''
                                "
                                :name="`privacyPolicyNotice.${locale}`"
                                :label="$t('label.privacyPolicyNotice')"
                                :placeholder="$t('placeholder.insert')"
                                :hint-label="$t('hint.privacyPolicyNotice')"
                                class="mb-0"
                                @update:model-value="
                                    (val: string | undefined) =>
                                        (privacyPolicyNotice = {
                                            ...privacyPolicyNotice,
                                            [locale]: val ?? '',
                                        })
                                " />
                        </template>
                    </VvTab>
                </template>
            </template>
            <template #panel::tools>
                <PkFieldset :legend="$t('label.fileUpload')">
                    <PkCheckboxField
                        v-model="fileUploadEnabled"
                        :disabled="playground"
                        name="fileUploadEnabled"
                        switch
                        :label="$t('label.fileUploadEnabled')"
                        :hint-label="$t('hint.fileUploadEnabled')" />
                    <template v-if="fileUploadEnabled">
                        <VvInputText
                            v-model="fileSizeMB"
                            :disabled="playground"
                            name="fileUploadMaxFileSize"
                            type="number"
                            :min="1"
                            :max="100"
                            :label="$t('label.fileUploadMaxFileSize')"
                            :placeholder="$t('placeholder.insert')" />
                        <VvCombobox
                            v-model="selectedMimeCategories"
                            :disabled="playground"
                            name="fileUploadAllowedMimeTypes"
                            :label="$t('label.fileUploadAllowedMimeTypes')"
                            :placeholder="$t('placeholder.select')"
                            :options="mimeTypeOptions"
                            badges
                            multiple
                            strategy="fixed" />
                        <div class="grid sm:grid-cols-2 gap-md items-start">
                            <VvInputText
                                v-model="fileUploadMaxFilesPerMessage"
                                :disabled="playground"
                                name="fileUploadMaxFilesPerMessage"
                                type="number"
                                :min="1"
                                :max="10"
                                :label="
                                    $t('label.fileUploadMaxFilesPerMessage')
                                "
                                :placeholder="$t('placeholder.insert')" />
                            <VvInputText
                                v-model="fileUploadMaxFilesPerChat"
                                :disabled="playground"
                                name="fileUploadMaxFilesPerChat"
                                type="number"
                                :min="1"
                                :max="50"
                                :label="$t('label.fileUploadMaxFilesPerChat')"
                                :placeholder="$t('placeholder.insert')" />
                        </div>
                    </template>
                </PkFieldset>
                <PkFieldset :legend="$t('label.webSearch')">
                    <PkWebSearchSettings
                        v-model:web-search="webSearch"
                        :active-provider />
                </PkFieldset>
                <div class="flex flex-col gap-md">
                    <!-- Stated once for the whole tab: the per-tool label says
                         what the option does, this says what it means per
                         channel (see docs/specs/tool-approval-interface.md). -->
                    <p class="text-12 text-word-3 mb-sm">
                        {{ $t('hint.approvalChannels') }}
                    </p>
                    <template v-for="group in toolGroups" :key="group.key">
                        <PkFieldset
                            v-if="
                                isChatAgent
                                    ? true
                                    : !group.tools.every((item) =>
                                          isPresentationTool(item),
                                      )
                            "
                            class="mb-0">
                            <template #legend>
                                <span class="flex items-center gap-4">
                                    <VvIcon
                                        :name="group.icon"
                                        class="text-16" />
                                    {{ $t(`toolGroup.${group.key}`) }}
                                </span>
                            </template>
                            <p class="text-12 text-word-3 mb-sm">
                                {{ $t(`toolGroupInfo.${group.key}`) }}
                            </p>
                            <div class="flex flex-col nx-md nb-md">
                                <template
                                    v-for="tool in group.tools"
                                    :key="tool">
                                    <template
                                        v-if="
                                            isPresentationTool(tool)
                                                ? isChatAgent
                                                : true
                                        ">
                                        <VvCheckbox
                                            class="flex-nowrap border-t border-surface-3 mr-0 px-md pt-md"
                                            :model-value="isToolEnabled(tool)"
                                            :name="`tool.${tool}`"
                                            :value="true"
                                            :unchecked-value="false"
                                            :disabled="!isToolAvailable(tool)"
                                            switch
                                            @update:model-value="
                                                (val: boolean) =>
                                                    toggleTool(tool, val)
                                            ">
                                            <div
                                                class="flex flex-col ml-12 gap-2">
                                                <div
                                                    class="flex items-center gap-8">
                                                    <strong
                                                        class="font-semibold">
                                                        {{
                                                            $t(
                                                                `agent.tool.${tool}`,
                                                            )
                                                        }}
                                                    </strong>
                                                    <VvBadge
                                                        v-if="
                                                            tool ===
                                                            AgentBuiltInTool.ShowMultipleChoice
                                                        "
                                                        modifiers="gray sm">
                                                        {{
                                                            $t(
                                                                'label.deprecated',
                                                            )
                                                        }}
                                                    </VvBadge>
                                                    <PkToolApprovalBadge
                                                        v-if="
                                                            requiresApproval(
                                                                tool,
                                                            )
                                                        " />
                                                </div>
                                                <small
                                                    class="text-12 text-word-3">
                                                    {{
                                                        $t(`hint.tool.${tool}`)
                                                    }}
                                                </small>
                                                <!-- Channel-rendering hint: only
                                                     meaningful for presentation
                                                     tools (non-show tools always
                                                     work on every channel). One
                                                     ghost badge per channel it
                                                     renders on natively. -->
                                                <div
                                                    v-if="
                                                        isPresentationTool(
                                                            tool,
                                                        ) &&
                                                        isPlatformRenderedTool(
                                                            tool,
                                                        )
                                                    "
                                                    class="flex items-center gap-4 self-start mt-4 pt-4 w-full border-t border-surface-3"
                                                    :title="
                                                        $t(
                                                            'hint.platformSupported',
                                                        )
                                                    ">
                                                    <VvBadge
                                                        modifiers="ghost sm"
                                                        class="inline-flex items-center gap-4">
                                                        <VvIcon
                                                            name="ri:whatsapp-line"
                                                            class="text-12" />
                                                        {{
                                                            $t('label.whatsapp')
                                                        }}
                                                    </VvBadge>
                                                    <VvBadge
                                                        modifiers="ghost sm"
                                                        class="inline-flex items-center gap-4">
                                                        <VvIcon
                                                            name="ri:microsoft-line"
                                                            class="text-12" />
                                                        {{ $t('label.teams') }}
                                                    </VvBadge>
                                                </div>
                                            </div>
                                        </VvCheckbox>
                                        <!-- One accordion per tool for every extra option, the
                                             pattern showForm already used: the tool list stays
                                             scannable and the options appear where they belong,
                                             without repeating a sub-option row per tool. -->
                                        <VvAccordion
                                            v-if="
                                                hasAdvancedOptions(tool) &&
                                                isToolEnabled(tool)
                                            "
                                            :title="$t('label.advancedOptions')"
                                            modifiers="details"
                                            class="border border-surface-3 rounded-lg mx-md mb-md">
                                            <div
                                                class="p-xs flex gap-md items-start">
                                                <template
                                                    v-if="
                                                        tool ===
                                                        AgentBuiltInTool.ShowForm
                                                    ">
                                                    <VvInputText
                                                        v-model="formMaxSteps"
                                                        name="formMaxSteps"
                                                        type="number"
                                                        :min="1"
                                                        :max="10"
                                                        :label="
                                                            $t(
                                                                'label.formMaxSteps',
                                                            )
                                                        "
                                                        :disabled="playground"
                                                        class="mb-0" />
                                                    <VvInputText
                                                        v-model="
                                                            formMaxOptionsPerStep
                                                        "
                                                        name="formMaxOptionsPerStep"
                                                        type="number"
                                                        :min="2"
                                                        :max="10"
                                                        :label="
                                                            $t(
                                                                'label.formMaxOptionsPerStep',
                                                            )
                                                        "
                                                        :disabled="playground"
                                                        class="mb-0" />
                                                </template>
                                                <!-- Cross-agent: read the user's whole KB across agents -->
                                                <PkCheckboxField
                                                    v-if="
                                                        tool ===
                                                        AgentBuiltInTool.UserMemory
                                                    "
                                                    v-model="useAllUserKb"
                                                    name="useAllUserKb"
                                                    :label="
                                                        $t(
                                                            'agent.tool.useAllUserKb',
                                                        )
                                                    "
                                                    :hint-label="
                                                        $t(
                                                            'hint.tool.useAllUserKb',
                                                        )
                                                    "
                                                    :disabled="playground" />
                                                <!-- Cross-agent: search all the user's files across agents -->
                                                <PkCheckboxField
                                                    v-if="
                                                        tool ===
                                                        AgentBuiltInTool.SearchInUserFiles
                                                    "
                                                    v-model="useAllUserFiles"
                                                    name="useAllUserFiles"
                                                    :label="
                                                        $t(
                                                            'agent.tool.useAllUserFiles',
                                                        )
                                                    "
                                                    :hint-label="
                                                        $t(
                                                            'hint.tool.useAllUserFiles',
                                                        )
                                                    "
                                                    :disabled="playground" />
                                                <!-- Approval: only for tools that
                                                     run server-side, see
                                                     canRequireApproval. -->
                                                <PkCheckboxField
                                                    v-if="
                                                        canRequireApproval(tool)
                                                    "
                                                    :model-value="
                                                        requiresApproval(tool)
                                                    "
                                                    :name="`tool.${tool}.approval`"
                                                    :label="
                                                        $t(
                                                            'label.toolRequiresApproval',
                                                        )
                                                    "
                                                    :hint-label="
                                                        $t(
                                                            'hint.toolRequiresApproval',
                                                        )
                                                    "
                                                    :disabled="playground"
                                                    class="mb-0"
                                                    @update:model-value="
                                                        (val: boolean) =>
                                                            toggleApproval(
                                                                tool,
                                                                val,
                                                            )
                                                    " />
                                            </div>
                                        </VvAccordion>
                                    </template>
                                </template>
                            </div>
                        </PkFieldset>
                    </template>
                </div>
            </template>
            <template #panel::embed>
                <div class="flex flex-col gap-md">
                    <PkInfobox
                        v-if="!channels.includes(AgentChannel.Widget)"
                        level="warning"
                        class="mb-0">
                        {{ $t('hint.channelWidgetDisabled') }}
                    </PkInfobox>
                    <PkInfobox
                        v-if="!channels.includes(AgentChannel.App)"
                        level="warning"
                        class="mb-0">
                        {{ $t('hint.channelAppDisabled') }}
                    </PkInfobox>
                    <div>
                        <h3 class="font-semibold mb-xs">
                            {{ $t('label.chatWidget') }}
                        </h3>
                        <p class="text-smaller">
                            {{ $t('hint.embedCode') }}
                        </p>
                    </div>
                    <PkCopyBlock :source="embedCode">
                        <PkCode
                            :code="embedCode"
                            language="html"
                            line
                            class="text-12 overflow-hidden font-mono leading-snug bg-surface-1" />
                    </PkCopyBlock>
                    <div v-if="!playground">
                        <VvInputText
                            v-model="widgetUrl"
                            name="widgetUrl"
                            type="url"
                            icon="ri:links-line"
                            :label="$t('label.widgetUrl')"
                            :placeholder="DEFAULT_WIDGET_URL" />
                        <!-- The snippet and where it loads from is the whole
                         job; how it loads is a decision taken once, by whoever
                         knows the host page -->
                        <VvAccordion
                            :title="$t('label.advancedOptions')"
                            modifiers="details"
                            class="border border-surface-3 rounded-lg mb-md">
                            <div class="p-8 flex flex-col">
                                <VvInputText
                                    v-model="loadDelay"
                                    name="loadDelay"
                                    type="number"
                                    step="100"
                                    min="0"
                                    icon="ri:time-line"
                                    class="mb-0"
                                    :label="$t('label.loadDelay')"
                                    :hint-label="$t('hint.loadDelay')"
                                    :placeholder="'0'" />
                                <VvCombobox
                                    v-model="loaderStrategy"
                                    name="loaderStrategy"
                                    icon="ri:code-line"
                                    class="mt-md mb-0"
                                    :label="$t('label.loaderStrategy')"
                                    :options="[
                                        {
                                            label: $t(
                                                'label.loaderStrategyInlineModule',
                                            ),
                                            value: AgentLoaderStrategy.InlineModule,
                                        },
                                        {
                                            label: $t(
                                                'label.loaderStrategyScriptSrc',
                                            ),
                                            value: AgentLoaderStrategy.ScriptSrc,
                                        },
                                        {
                                            label: $t(
                                                'label.loaderStrategyDynamicImport',
                                            ),
                                            value: AgentLoaderStrategy.DynamicImport,
                                        },
                                    ]" />
                                <PkInfobox
                                    v-if="loaderStrategy"
                                    class="mt-md mb-0">
                                    {{
                                        $t(
                                            `hint.loaderStrategy.${loaderStrategy}`,
                                        )
                                    }}
                                </PkInfobox>
                            </div>
                        </VvAccordion>
                        <PkFieldset
                            :legend="$t('label.externalContext')"
                            class="mb-0">
                            <p class="text-smaller mb-xs">
                                {{ $t('hint.externalContextSchema') }}
                            </p>
                            <PkCheckboxField
                                v-model="isExternalContextEnabled"
                                name="externalContextEnabled"
                                switch
                                :label="$t('label.externalContextSchema')"
                                :hint-label="
                                    $t('hint.externalContextSchemaToggle')
                                " />
                            <VvButton
                                v-if="isExternalContextEnabled"
                                modifiers="action-small"
                                icon="ri:code-line"
                                class="ml-auto"
                                :label="$t('action.editSchema')"
                                @click="
                                    isExternalContextDialogVisible = true
                                " />
                            <VvDialog
                                v-model="isExternalContextDialogVisible"
                                :title="$t('label.externalContextSchema')"
                                keep-open>
                                <PkEditorJsonSchema
                                    v-model="externalContextSchema"
                                    :label="$t('label.externalContextSchema')"
                                    :placeholder="$t('placeholder.insert')" />
                                <template #footer>
                                    <VvButton
                                        modifiers="primary"
                                        :label="$t('action.close')"
                                        @click="
                                            isExternalContextDialogVisible = false
                                        " />
                                </template>
                            </VvDialog>
                        </PkFieldset>
                    </div>
                    <div>
                        <h3 class="font-semibold mb-xs">
                            {{ $t('label.iframe') }}
                        </h3>
                        <p class="text-smaller">
                            {{ $t('hint.embedIframe') }}
                        </p>
                    </div>
                    <PkCopyBlock :source="iframeEmbedCode">
                        <PkCode
                            :code="iframeEmbedCode"
                            language="html"
                            line
                            class="text-12 overflow-hidden font-mono leading-snug bg-surface-1" />
                    </PkCopyBlock>
                    <div>
                        <h4 class="font-semibold mb-xs">
                            {{ $t('label.preview') }}
                        </h4>
                        <p class="text-smaller">
                            {{ $t('hint.embedPreview') }}
                        </p>
                    </div>
                    <PkCopyBlock :source="chatUrl">
                        <code
                            class="block text-12 text-word-2 break-all p-12 font-mono">
                            {{ chatUrl }}
                        </code>
                        <template #actions>
                            <VvButton
                                modifiers="action-quiet-small"
                                icon="ri:eye-line"
                                :label="$t('action.view')"
                                :href="chatUrl"
                                target="_blank" />
                        </template>
                    </PkCopyBlock>
                </div>
            </template>
            <template #panel::security>
                <PkFieldset :legend="$t('label.rateLimits')">
                    <div class="grid sm:grid-cols-2 gap-md">
                        <VvInputText
                            v-model="minuteRateLimit"
                            name="minuteRateLimit"
                            type="number"
                            :label="$t('label.minuteRateLimit')"
                            :placeholder="$t('placeholder.insert')"
                            :min="0"
                            :invalid="
                                getErrorMessages(
                                    'security.rateLimits.minuteRateLimit',
                                ).length > 0
                            "
                            :invalid-label="
                                getErrorMessages(
                                    'security.rateLimits.minuteRateLimit',
                                ).join(', ')
                            " />
                        <VvInputText
                            v-model="hourRateLimit"
                            name="hourRateLimit"
                            type="number"
                            :label="$t('label.hourRateLimit')"
                            :placeholder="$t('placeholder.insert')"
                            :min="0"
                            :invalid="
                                getErrorMessages(
                                    'security.rateLimits.hourRateLimit',
                                ).length > 0
                            "
                            :invalid-label="
                                getErrorMessages(
                                    'security.rateLimits.hourRateLimit',
                                ).join(', ')
                            " />
                        <VvInputText
                            v-model="dayRateLimit"
                            name="dayRateLimit"
                            type="number"
                            :label="$t('label.dayRateLimit')"
                            :placeholder="$t('placeholder.insert')"
                            :min="0"
                            :invalid="
                                getErrorMessages(
                                    'security.rateLimits.dayRateLimit',
                                ).length > 0
                            "
                            :invalid-label="
                                getErrorMessages(
                                    'security.rateLimits.dayRateLimit',
                                ).join(', ')
                            " />
                        <VvInputText
                            v-model="monthRateLimit"
                            name="monthRateLimit"
                            type="number"
                            :label="$t('label.monthRateLimit')"
                            :placeholder="$t('placeholder.insert')"
                            :min="0"
                            :invalid="
                                getErrorMessages(
                                    'security.rateLimits.monthRateLimit',
                                ).length > 0
                            "
                            :invalid-label="
                                getErrorMessages(
                                    'security.rateLimits.monthRateLimit',
                                ).join(', ')
                            " />
                    </div>
                </PkFieldset>
                <PkEditorRepeater
                    v-slot="{ index }"
                    v-model="trustedOrigins.origins"
                    name="trustedOrigins"
                    :label="$t('label.trustedOrigins')"
                    :placeholder="$t('placeholder.noTrustedOrigins')"
                    :invalid="
                        getErrorMessages('security.trustedOrigins.origins')
                            .length > 0
                    "
                    :invalid-label="
                        getErrorMessages(
                            'security.trustedOrigins.origins',
                        ).join(', ')
                    "
                    :confirm="false"
                    :default-value="() => ''"
                    allow-empty>
                    <VvInputText
                        v-model="trustedOrigins.origins![index]"
                        :name="`trustedOrigins.${index}`"
                        :label="$t('label.origin')"
                        :placeholder="$t('placeholder.insertOrigin')"
                        :invalid="
                            getErrorMessages(
                                `security.trustedOrigins.origins.${index}`,
                            ).length > 0
                        "
                        :invalid-label="
                            getErrorMessages(
                                `security.trustedOrigins.origins.${index}`,
                            ).join(', ')
                        "
                        floating />
                </PkEditorRepeater>
            </template>
        </VvTab>
        <slot />
    </div>
</template>

<style lang="scss">
    .pk-agent-settings-panel {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: var(--spacing-md);
        min-height: 0;

        .vv-tab {
            flex: 1;
            overflow: auto;
            display: flex;
            flex-direction: column;
            min-height: 0;

            &__panel {
                flex: 1;
                overflow-y: auto;
            }
        }
    }
</style>
